import { useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { IoBuild, IoCloseCircleOutline } from 'react-icons/io5';
import { Tooltip } from 'react-tooltip';
import { getArea } from 'ol/sphere';

import { useAuth } from '@contexts/User.context';
import { useProject } from '@contexts/Project.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';
import { PROJECT_MODE } from '@utils/constants';

import {
	setupFreehandDraw,
	removeFreehandDraw,
	checkAreaOfInterest,
} from '@utils/map/freeHand.draw';
import { clearTooltip } from '@utils/map/tooltip.overlay';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';
import { formatArea } from '@utils/measurementSystem';

import { getGridFeatures } from '@utils/api';
import AnnotateOrthophoto from '../ai/AnnotateOrthophoto';
import AnnotateSingleImages from '../ai/AnnotateSingleImages';
import { resetAnnotationRefs } from '@utils/map/annotation/annotation.interaction';
import { useToast } from '@contexts/Toast.context';

const annotationSidebarId = 'annotationSidebar';

export default function Annotate() {
	const { addToast } = useToast();
	const {
		project,
		projectMode,
		detections,
		pickedTask,
		toolBarVisible,
		isDemo,
		mapObject,
		mapTooltip,
		annotationMode,
		setActiveSidebar,
		removeFromActiveSidebars,
		selectedSingleImage,
		dispatch,
	} = useProject();

	const {
		tierTrial,
		trialEnded,
		tierPremium,
		tierPro,
		subscriptionActive,
		isEnterprise,
		activeOrgAccess,
		userAccess,
	} = useAuth();

	// `toolBarVisible` already keeps this and Detect-in-area out of each other's
	// way and covers the single-image case, which map ownership does not. This adds
	// the owners it does not cover — a boundary edit, and measurement mode. Only
	// the start branch is gated; cancelling must stay reachable once the flow owns
	// the map itself.
	const { busy: mapBusy, reason: blockedReason } = useMapToolBusy();

	const { tooltip, tooltipRef } = mapTooltip || {};

	// layers to hide during annotation
	const layersToHide = [
		'aoiLayer',
		'dsmLayer',
		'hexagonLayer',
		'trainingDataLayer',
		'centerLayer',
		'outlineLayer',
		'detailsLayer',
	];

	// layers to show after annotation is stopped
	const [layersToShow, setLayersToShow] = useState([]);

	// The boundary-draw step that precedes annotation mode. Held in a ref because
	// both cancel affordances (the toolbar button and ESC) run outside the closure
	// that sets the draw interaction up.
	const drawRef = useRef(null);
	const [isDrawing, setIsDrawing] = useState(false);

	// Guards the window between the button being clicked and the draw step being
	// set up. A ref rather than state because the two awaits below resolve before
	// React has re-rendered, so a state flag would still read false.
	const preparingRef = useRef(false);

	const getLayersToShow = () => {
		const layers = mapObject.getLayers()?.getArray();
		if (!layers?.length) return [];

		// Gets an array of customLayerId strings that are visible
		return layers.reduce((acc, layer) => {
			const { customLayerId, visible } = layer?.getProperties() ?? {};
			if (customLayerId && visible) acc.push(customLayerId);
			return acc;
		}, []);
	};

	const annotationButtonTooltipText = () => {
		if (tierTrial) {
			if (trialEnded) return 'Your trial has ended and you can no longer annotate';
			if (pickedTask?.multimodel) return 'You can not train a multimodel as a trial user';
			if (pickedTask?.is_trained) return 'You can not improve accuracy as a trial user';
		}

		if (detections.length === 0) return 'Make manual annotations to train the AI model';

		return 'Enhance AI accuracy by fine-tuning the annotations';
	};

	const checkTrainingPermissions = async () => {
		const showAlert = msg => {
			addToast({
				id: 'training-permission-denied',
				bg: 'warning',
				title: msg,
			});
			return false;
		};

		if (tierTrial) {
			if (trialEnded) return showAlert('Your trial has ended and you can no longer annotate');

			if (pickedTask?.multimodel)
				return showAlert('You cannot train a multimodel as a trial user');

			if (pickedTask?.is_trained)
				return showAlert('You cannot improve accuracy as a trial user');

			// Use mapObject.getLayers() to check if training data layer exists
			const layers = mapObject.getLayers()?.getArray();
			const trainingDataLayerExists = layers?.some(
				layer => layer.get('name') === 'Training data'
			);

			if (trainingDataLayerExists)
				return showAlert(
					'Training data exists. You can only train a model once as a trial user.'
				);
		}

		if (tierPremium && !userAccess?.includes('train'))
			return showAlert('Your user does not have access rights to train AI');

		if (tierPro && !subscriptionActive)
			return showAlert('Your user needs an active subscription to train AI');

		if (isEnterprise) {
			// Check if activeOrgAccess array contains "all" or "train"
			const hasOrgAccess =
				activeOrgAccess.includes('all') || activeOrgAccess.includes('train');

			if (!hasOrgAccess)
				return showAlert('Your user does not have access rights to train AI');
		}

		return true; // Allow training if none of the conditions are met
	};

	const enterBoundaryDraw = async () => {
		const isTrainingAllowed = await checkTrainingPermissions();
		if (!isTrainingAllowed) return;

		const grid = await getGridFeatures({
			project_uuid: project.uuid,
			model_uuid: pickedTask.model_uuid,
		});

		if (!grid || grid.length === 0) {
			console.error('Could not fetch grid for training');
			addToast({
				id: 'training-grid-error',
				bg: 'danger',
				title: 'An error occurred and training could not be started. Please try again later.',
			});
			return;
		}

		const existingLayersVisibility = getLayersToShow();
		setLayersToShow(existingLayersVisibility);

		// Hide all layers that should not be visible during annotation
		setLayersVisibilityByCustomIds(mapObject, layersToHide, false);

		dispatch({
			type: 'setToolBarVisible',
			payload: false,
		});

		dispatch({
			type: 'setInteractionsDisabled',
			payload: true,
		});

		dispatch({
			type: 'setDialogue',
			payload: {
				header: 'Set a boundary',
				body: 'Please draw a boundary around the area in the map where you want to make annotations. Press ESC to cancel.',
				fullWidth: false,
				autoClose: true,
			},
		});

		const { draw, layer, source } = setupDraw({
			mapObject,
			tooltip,
			tooltipRef,
		});

		// Snapshotting the visibility here rather than reading layersToShow, because
		// that state is not up to date yet.
		drawRef.current = {
			draw,
			layer,
			layersVisibility: existingLayersVisibility,
		};
		setIsDrawing(true);

		// Draw the area of interest
		// Recursively run the promise until it returns true
		const drawAreaUntilTrue = async () => {
			const gridFeatures = await drawAOI({
				grid,
				draw,
				source,
				mapTooltip,
				mode: projectMode,
				addToast,
			});
			if (!gridFeatures) {
				await drawAreaUntilTrue(); // Recurse
				return;
			}

			// Clear the draw interaction and source
			cleanupDraw({ draw, layer });
			// Leaving the draw step also drops the ESC handler and the Cancel button
			drawRef.current = null;
			setIsDrawing(false);

			// Allow training to start
			allowTraining(gridFeatures);
		};
		drawAreaUntilTrue();
	};

	/**
	 * Entry point for the Train AI button. Permissions and the grid are fetched
	 * before the toolbar hides, so the button stays clickable through that window;
	 * without this guard a second click starts a second draw whose interaction
	 * overwrites drawRef, stranding the first one on the map with neither ESC nor
	 * Cancel able to reach it.
	 */
	const prepareTraining = async () => {
		if (preparingRef.current || drawRef.current) return;
		preparingRef.current = true;

		try {
			await enterBoundaryDraw();
		} finally {
			// The flow returns once the draw step is live; drawRef guards from there.
			preparingRef.current = false;
		}
	};

	const allowTraining = gridFeatures => {
		const taskLabels = pickedTask.classes.map(task => task.description);
		const taskIds = Array.from(Array(taskLabels.length).keys());

		dispatch({
			type: 'setAnnotationMode',
			payload: {
				gridFeatures,
			},
		});

		dispatch({
			type: 'setAnnotationSidebarData',
			payload: {
				classIds: taskIds,
				labels: taskLabels,
				activeClassId: taskIds[0],
			},
		});

		setActiveSidebar({ sidebarId: annotationSidebarId });
	};

	const cleanupDraw = ({ draw, layer }) => {
		// Clear the draw interaction and source
		dispatch({
			type: 'setDialogue',
			payload: null,
		});

		dispatch({
			type: 'setInteractionsDisabled',
			payload: false,
		});

		removeFreehandDraw({ mapRef: mapObject, draw, layer });
		clearTooltip({ ...mapTooltip });

		// Reset any annotation references that may have been set before
		resetAnnotationRefs();
	};

	/**
	 * Leaves the boundary-draw step without entering annotation mode. Backs both
	 * cancel affordances: the toolbar's Cancel button and the ESC key.
	 */
	const cancelTrainingFlow = () => {
		const { draw, layer, layersVisibility } = drawRef.current ?? {};
		drawRef.current = null;
		setIsDrawing(false);

		if (draw && layer) cleanupDraw({ draw, layer });

		dispatch({
			type: 'setToolBarVisible',
			payload: true,
		});

		if (layersVisibility) setLayersVisibilityByCustomIds(mapObject, layersVisibility, true);
	};

	useKeyboardShortcut('Escape', isDrawing, cancelTrainingFlow);

	if (!mapObject || !pickedTask || selectedSingleImage) return null;

	if (annotationMode) {
		if (projectMode === PROJECT_MODE.ORTHOPHOTO) {
			return (
				<AnnotateOrthophoto
					layersToShow={layersToShow}
					hideAnnotationSidebar={() => {
						removeFromActiveSidebars(annotationSidebarId);
					}}
				/>
			);
		} else if (projectMode === PROJECT_MODE.SINGLE_IMAGE) {
			return <AnnotateSingleImages layersToShow={layersToShow} />;
		}
	}

	// Both states share the toolbar's button chrome; only these four values differ.
	const isImproving = detections.length > 0;
	const action = isDrawing
		? {
				onClick: cancelTrainingFlow,
				disabled: isDemo,
				icon: <IoCloseCircleOutline className="m-1 mb-2" />,
				label: isImproving ? 'Cancel Accuracy Improvement' : 'Cancel Training',
				tooltip: isImproving
					? 'Cancel the accuracy improvement'
					: 'Cancel training and keep the current model',
			}
		: {
				onClick: prepareTraining,
				disabled: !toolBarVisible || isDemo || mapBusy,
				icon: <IoBuild className="m-1 mb-2" style={{ transform: 'rotate(-90deg)' }} />,
				label: isImproving ? 'Improve AI Accuracy' : 'Train AI',
				tooltip: blockedReason || annotationButtonTooltipText(),
			};

	return (
		<>
			<Tooltip id="annotate" variant="light" />
			<Button
				disabled={action.disabled}
				onClick={action.onClick}
				variant="dark-secondary"
				className="btn-dark secondary"
				data-tooltip-id="tooltip-toolbar-root"
				data-tooltip-content={action.tooltip}
				data-tooltip-place="top">
				{action.icon}
				{action.label}
			</Button>
		</>
	);
}

/**
 * Formats the area of a polygon geometry feature as a human-readable string.
 * Uses the OpenLayers getArea function to calculate the area of the geometry,
 * then formats it using the measurement system set in the application.
 *
 * @param {import('ol/geom').Geometry} geometry - The OpenLayers geometry object to measure
 * @returns {string} Formatted string showing the area with appropriate units (e.g., "Area: 10.50 m²")
 */
const getAreaString = geometry => {
	const [area, unit] = formatArea(getArea(geometry));
	return `${area} ${unit}`;
};

const setupDraw = ({ mapObject, tooltip, tooltipRef }) => {
	// Setup draw interaction
	const { draw, layer, source } = setupFreehandDraw({
		type: 'Polygon',
		mapRef: mapObject,
		freehand: false,
	});

	const freehandRef = {
		draw,
		layer,
		source,
	};

	// Add drawstart event listener add tooltip to the map
	draw.on('drawstart', function (e) {
		const sketch = e.feature;

		sketch.getGeometry().on('change', function (evt) {
			const geom = evt.target;

			// Set the tooltip content to the area of the geometry
			tooltipRef.textContent = getAreaString(geom);

			const tooltipCoord = geom.getInteriorPoint().getCoordinates();
			tooltip.setPosition(tooltipCoord);
		});
	});

	return freehandRef;
};

const drawAOI = async ({ grid, draw, source, mapTooltip, mode, addToast }) => {
	return new Promise(resolve => {
		checkAreaOfInterest({ mode, grid, draw })
			.then(coordinates => {
				resolve(coordinates);
			})
			.catch(error => {
				console.warn(error);
				addToast({
					id: 'draw-aoi-error',
					bg: 'danger',
					title: error,
				});

				// Clear the draw interaction and source
				source.clear();
				clearTooltip({ ...mapTooltip });

				resolve(false);
			});
	});
};
